import { Chips, Choice, MultiChoice, Separator, useTranslationContext } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';
import { match, Pattern } from 'ts-pattern';
import { always, intersection } from 'ramda';
import { isArray } from 'ramda-adjunct';
import { Nullish, suffixTestId, useOnMount } from 'shared/web';
import { quickFiltersConst } from '../../constants/quickFilters';
import { useChoiceFilter } from '../../hooks/useChoiceFilter';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import { useLazyChoiceFilterOptions } from '../../hooks/useLazyChoiceFilterOptions';
import { EagFilterProps } from '../../types/EagFilterProps';
import { getFilterFieldName } from '../../utils/getFilterFieldName';
export type ChoiceFilterProps = EagFilterProps & {
  columnKey: string;
  isMulti: boolean;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
const FILTER_VALUE_KEY = 'value';
export function ChoiceFilter(props: ChoiceFilterProps) {
  const t = useTranslationContext();
  const rawModel = props.value[FILTER_VALUE_KEY] as string[] | string | Nullish;
  const [values, setValues] = useChoiceFilter({
    model: rawModel,
    onChange: newValue => {
      props.onChange({
        [FILTER_VALUE_KEY]: newValue
      });
    }
  });
  const {
    loadOptions,
    isLoading,
    options,
    errorMessage
  } = useLazyChoiceFilterOptions({
    columnKey: props.columnKey
  });
  const {
    onChipsChange
  } = useFilterOnChipsChange({
    setFilterValue: setValues,
    defaultValue: null
  });
  const isQuickFilterValue = intersection(isArray(values) ? values : [values], quickFiltersConst);
  const onChange = (changedValues: string[] | string | number | null) => {
    const correctedValues = match<string[] | string | number | null, string[] | Nullish>(changedValues).with(Pattern.array(Pattern.string), v => v).with(Pattern.number, v => [String(v)]).with(Pattern.string, v => [v]).otherwise(always(null));
    setValues(correctedValues);
  };
  const isControlDisabled = props.isDisabled || isLoading;
  useOnMount(() => {
    void loadOptions();
  });
  return <VStack>
      {props.quickFilters && props.quickFilters.length > 0 && <>
          <HStack>
            <Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId('choice-filter-quickFilters', props)} isDeselectable />
          </HStack>
          <Separator />
        </>}
      {props.isMulti ? <MultiChoice options={options} value={isArray(values) ? values : [values]} onChange={onChange} placeholder={t('page.datagrid.filter.choicePlaceholder', {
      field: getFilterFieldName(props.column)
    })} isDisabled={isControlDisabled} isLoading={isLoading} errorMessage={errorMessage} menuInPortal data-testid={suffixTestId('choice-filter', props)} /> : <Choice options={options} value={isArray(values) ? values[0] : values} onChange={onChange} placeholder={t('page.datagrid.filter.choicePlaceholder', {
      field: getFilterFieldName(props.column)
    })} isDisabled={isControlDisabled} isLoading={isLoading} errorMessage={errorMessage} menuInPortal data-testid={suffixTestId('choice-filter', props)} />}
    </VStack>;
}